import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAppStore } from '../store/appStore';
import SessionLoader from './SessionLoader';
import { CHANGE_PASSWORD_PATH } from '../hooks/useAuth';

interface RequireAuthProps {
  children: React.ReactElement;
  /**
   * Set on the forced password-change route itself, so the guard below doesn't
   * bounce it back to itself. Every other protected route leaves this off.
   */
  allowPasswordChange?: boolean;
}

const RequireAuth: React.FC<RequireAuthProps> = ({ children, allowPasswordChange = false }) => {
  const user = useAppStore((state) => state.user);
  const isAuthLoading = useAppStore((state) => state.isAuthLoading);
  const location = useLocation();

  if (isAuthLoading) {
    return <SessionLoader />;
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // A user still on a temp password can't reach anything else. This block is
  // frontend-only — the backend doesn't hard-block other endpoints while the
  // flag is set (see FRONTEND_RBAC_GUIDE.md §5), so it has to live here.
  if (user.mustChangePassword && !allowPasswordChange) {
    return <Navigate to={CHANGE_PASSWORD_PATH} replace />;
  }

  return children;
};

export default RequireAuth;
